// TIMEREG. HOOKS
import {
	GetDrivingSlipCategoriesQuery,
	GetPaymentSupplementsQuery,
	GetTimeTrackingDayClosedByUserQuery,
	GetTimeTrackingDaysQuery,
	GetTimeTrackingsDayForUserDocument,
	GetTimeTrackingsDayForUserQuery,
	GetTimeTrackingsDayForUserQueryVariables,
	GetUserFlexTimeQuery,
	GetWebCasesByErpNoQuery,
	GetWebCasesForTimeTrackingQuery,
	GetWebDrivingSlipQuery,
	GetWebDrivingSlipsNoCaseQuery,
	GetrejectedTimeTrackingsDayForUserQuery,
	JobTasksQuery,
	OpenRejectedTimeTrackingDayMutationVariables,
	SearchWebUsersQuery,
	SupplementType,
	TimeTrackingDayInputType,
	TimeTrackingDayStatus,
	TimeTrackingEntryInputType,
	TimeTrackingEntryUpdateInputType,
	TrackingType,
	UserBasisTimeQuery,
	useCreateTimeTrackingDayMutation,
	useDeleteTimeTrackingDayEntryMutation,
	useDeleteTimeTrackingDayMutation,
	useDeleteTimeTrackingEntrySupplementMutation,
	useGetDrivingSlipCategoriesQuery,
	useGetPaymentSupplementsQuery,
	useGetTimeTrackingDayClosedByUserQuery,
	useGetTimeTrackingDaysQuery,
	useGetTimeTrackingsDayForUserQuery,
	useGetUserFlexTimeQuery,
	useGetWebCasesByErpNoQuery,
	useGetWebCasesForTimeTrackingQuery,
	useGetWebDrivingSlipQuery,
	useGetWebDrivingSlipsNoCaseQuery,
	useGetrejectedTimeTrackingsDayForUserQuery,
	useJobTasksQuery,
	useOpenRejectedTimeTrackingDayMutation,
	useReopenTimeTrackingDayMutation,
	useSearchWebUsersQuery,
	useSubmitTimeTrackingDayMutation,
	useUpdateTimeTrackingDayEntryMutation,
	useUserBasisTimeQuery,
} from '@ssg/common/GraphQL/indexV2';
import { ApolloQueryResult } from '@apollo/client';
import { SelectOption } from '@ssg/common/Helpers/Helpers';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { shiftSupplements } from './TimeRegHelpers';

// QUERIES

// Get user timeregs. based on user id and selected date (status rejected are filtered out)
export function useTimeTrackingsDayForUserData(
	userId: string,
	selectedDate: string,
	rejectMode?: boolean,
): {
	timeTrackingsDayLoading: boolean;
	timeTrackingsDayDataRaw: GetTimeTrackingsDayForUserQuery | undefined;
	refetchTimeTrackingsDay: () => Promise<ApolloQueryResult<GetTimeTrackingsDayForUserQuery>>;
} {
	const {
		data: timeTrackingsDayDataRaw,
		loading: timeTrackingsDayLoading,
		refetch: refetchTimeTrackingsDay,
	} = useGetTimeTrackingsDayForUserQuery({
		variables: {
			user: userId,
			minDate: selectedDate,
			maxDate: selectedDate,
		},
		skip: selectedDate === '' || userId === '' || rejectMode,
	});

	return {
		timeTrackingsDayLoading: timeTrackingsDayLoading,
		timeTrackingsDayDataRaw: timeTrackingsDayDataRaw,
		refetchTimeTrackingsDay: refetchTimeTrackingsDay,
	};
}

// Get time tracking days based on user id and date range
export function useGetTimeTrackingDaysQueryData(
	minDate: string,
	maxDate: string,
	users?: string[] | string,
	timeTrackingDayStatus?: TimeTrackingDayStatus,
): {
	timeTrackingDaysLoading: boolean;
	timeTrackingDaysDataRaw: GetTimeTrackingDaysQuery | undefined;
	refetchTimeTrackingDays: () => Promise<ApolloQueryResult<GetTimeTrackingDaysQuery>>;
} {
	const {
		data: timeTrackingDaysDataRaw,
		loading: timeTrackingDaysLoading,
		refetch: refetchTimeTrackingDays,
	} = useGetTimeTrackingDaysQuery({
		variables: {
			minDate: minDate,
			maxDate: maxDate,
			users: users,
			timeTrackingDayStatus: timeTrackingDayStatus,
		},
	});

	return {
		timeTrackingDaysLoading: timeTrackingDaysLoading,
		timeTrackingDaysDataRaw: timeTrackingDaysDataRaw,
		refetchTimeTrackingDays: refetchTimeTrackingDays,
	};
}

// Get drivingSlips
export function useDrivingSlipsData(id: string | undefined): {
	drivingSlipLoading: boolean;
	drivingSlipDataRaw: GetWebDrivingSlipQuery | undefined;
} {
	const { data: drivingSlipDataRaw, loading: drivingSlipLoading } = useGetWebDrivingSlipQuery({
		variables: { id: id ?? '' },
		skip: id === '' || typeof id === 'undefined',
	});

	return {
		drivingSlipLoading: drivingSlipLoading,
		drivingSlipDataRaw: drivingSlipDataRaw,
	};
}

// Get user rejected timetrackings
export function useRejectedTimeTrackingsForUserQuery(
	userId: string,
	RejectedTimeRegistrationWarningFlag: boolean,
): {
	rejectedTimeTrackingLoading: boolean;
	rejectedTimeTrackingDataRaw: GetrejectedTimeTrackingsDayForUserQuery | undefined;
	refetchRejectedTimeTrackings: () => Promise<ApolloQueryResult<GetrejectedTimeTrackingsDayForUserQuery>>;
} {
	const {
		data: rejectedTimeTrackingDataRaw,
		loading: rejectedTimeTrackingLoading,
		refetch: refetchRejectedTimeTrackings,
	} = useGetrejectedTimeTrackingsDayForUserQuery({
		fetchPolicy: 'network-only',
		variables: { user: userId ?? '' },
		skip: userId === '' || typeof userId === 'undefined' || !RejectedTimeRegistrationWarningFlag,
	});

	return {
		rejectedTimeTrackingLoading: rejectedTimeTrackingLoading,
		rejectedTimeTrackingDataRaw: rejectedTimeTrackingDataRaw,
		refetchRejectedTimeTrackings: refetchRejectedTimeTrackings,
	};
}

// Get jobtasks from BC
export function useJobTasksDataQuery(erpReferenceNo: string): {
	jobTaskLoading: boolean;
	jobTaskDataRaw: JobTasksQuery | undefined;
} {
	const { data: jobTaskDataRaw, loading: jobTaskLoading } = useJobTasksQuery({
		fetchPolicy: 'cache-and-network',
		variables: {
			erpReferenceNo: erpReferenceNo,
		},
		skip: erpReferenceNo === '',
	});

	return {
		jobTaskLoading: jobTaskLoading,
		jobTaskDataRaw: jobTaskDataRaw,
	};
}

// Get closed day (also gets status rejected)
export function useClosedDayByUserQuery(userId: string): {
	closedDayRaw: GetTimeTrackingDayClosedByUserQuery | undefined;
	refetchClosedDay: () => Promise<ApolloQueryResult<GetTimeTrackingDayClosedByUserQuery>>;
} {
	const { data: closedDayRaw, refetch: refetchClosedDay } = useGetTimeTrackingDayClosedByUserQuery({
		fetchPolicy: 'no-cache',
		variables: {
			user: userId,
		},
		skip: userId === '',
	});

	return {
		closedDayRaw: closedDayRaw,
		refetchClosedDay: refetchClosedDay,
	};
}

// Get users
export function useSearchUsersQuery(
	searchText: string,
	onlyEmployees: boolean,
	checkInBc: boolean,
): {
	usersLoading: boolean;
	usersDataRaw: SearchWebUsersQuery | undefined;
} {
	const { data: usersDataRaw, loading: usersLoading } = useSearchWebUsersQuery({
		variables: {
			searchText: searchText,
			onlyEmployees: onlyEmployees,
			checkInBc: checkInBc,
		},
		skip: searchText === '',
	});

	return {
		usersLoading: usersLoading,
		usersDataRaw: usersDataRaw,
	};
}

// Get user basis time
export function useUserBasisTimeDataQuery(
	userId: string,
	selectedDate: string,
): {
	basisTimeRaw: UserBasisTimeQuery | undefined;
} {
	const { data: basisTimeRaw } = useUserBasisTimeQuery({
		fetchPolicy: 'cache-and-network',
		variables: {
			user: userId ?? '',
			date: selectedDate,
		},
		skip: userId === '' || selectedDate === '',
	});

	return {
		basisTimeRaw: basisTimeRaw,
	};
}

// Get user flex time
export function useUserFlexTimeQuery(userId: string): {
	userFlexTime: GetUserFlexTimeQuery | undefined;
} {
	const { data: userFlexTime } = useGetUserFlexTimeQuery({
		fetchPolicy: 'no-cache',
		variables: {
			user: userId ?? '',
		},
		skip: userId === '',
	});

	return {
		userFlexTime: userFlexTime,
	};
}

// Get cases data
export function useWebCasesForTimeTrackingQuery(
	erpReferenceNos: string[],
	includeEconomy: boolean,
	drivingSlipLoading: boolean,
	timeTrackingsDayLoading: boolean,
): {
	casesLoading: boolean;
	caseDataRaw: GetWebCasesForTimeTrackingQuery | undefined;
} {
	/* TODO: Find out if this is the wrong GQL */
	const { data: caseDataRaw, loading: casesLoading } = useGetWebCasesForTimeTrackingQuery({
		fetchPolicy: 'cache-and-network',
		variables: {
			erpReferenceNos: erpReferenceNos,
			includeEconomy: includeEconomy,
		},
		skip: drivingSlipLoading || timeTrackingsDayLoading || erpReferenceNos.length === 0,
	});

	return {
		casesLoading: casesLoading,
		caseDataRaw: caseDataRaw,
	};
}

// Get cases by erpNo
export function useWebCasesByErpNoQuery(
	caseSearchText: string,
	closedCases: boolean,
	appliedClosed: boolean,
): {
	erpNoCasesRaw: GetWebCasesByErpNoQuery | undefined;
	erpNoCasesLoading: boolean;
} {
	const { data: erpNoCasesRaw, loading } = useGetWebCasesByErpNoQuery({
		variables: {
			erpReferenceNo: '',
			closedCases: closedCases,
			appliedClosed: appliedClosed,
			searchString: caseSearchText,
		},
		skip: caseSearchText.length < 3,
	});

	return {
		erpNoCasesRaw: erpNoCasesRaw,
		erpNoCasesLoading: loading,
	};
}

// Get drivingslip no case
export function useWebDrivingSlipNoCaseQuery(
	erpNoCases: GetWebCasesByErpNoQuery | undefined,
	selectedCase: SelectOption | undefined,
	drivingSlipLoading: boolean,
	jobTask: string,
): {
	drivingSlipsNoCaseDataRaw: GetWebDrivingSlipsNoCaseQuery | undefined;
} {
	const { data: drivingSlipsNoCaseDataRaw } = useGetWebDrivingSlipsNoCaseQuery({
		variables: {
			case: erpNoCases?.cases.find(c => c.erpNo === selectedCase?.value)?.id,
		},
		skip: drivingSlipLoading || jobTask !== 'DRIVINGSLIP' || typeof selectedCase === 'undefined' || typeof erpNoCases === 'undefined',
	});

	return {
		drivingSlipsNoCaseDataRaw: drivingSlipsNoCaseDataRaw,
	};
}

// Get drivingslips categories
export function useDrivingSlipCategoriesQuery(): {
	drivingSlipCategoriesDataRaw: GetDrivingSlipCategoriesQuery | undefined;
} {
	const { data: drivingSlipCategoriesDataRaw } = useGetDrivingSlipCategoriesQuery();

	return {
		drivingSlipCategoriesDataRaw: drivingSlipCategoriesDataRaw,
	};
}

// Get payment supplements
export function usePaymentSupplementsQuery(): {
	paymentSupplementsDataRaw: GetPaymentSupplementsQuery | undefined;
} {
	const { data: paymentSupplementsDataRaw } = useGetPaymentSupplementsQuery();

	return {
		paymentSupplementsDataRaw: paymentSupplementsDataRaw,
	};
}

// MUTATIONS
// Create time tracking
export function useCreateTimeTracking(): {
	createTimeTrackingLoading: boolean;
	onSubmit: (timeTrackingDay: TimeTrackingDayInputType, timeTrackingEntry: TimeTrackingEntryInputType) => Promise<void>;
} {
	const [createTimeTracking, { loading: createTimeTrackingLoading }] = useCreateTimeTrackingDayMutation();

	const onSubmit = async (timeTrackingDay: TimeTrackingDayInputType, timeTrackingEntry: TimeTrackingEntryInputType) => {
		const getDayDate = () => DateTime.fromJSDate(new Date(timeTrackingDay.date));

		const dayDate = getDayDate();

		const convertedRangedEndDateToISO = timeTrackingDay.rangeEndDate ? DateTime.fromJSDate(new Date(timeTrackingDay.rangeEndDate)).toISO() : undefined;

		const startDate = DateTime.fromFormat(timeTrackingEntry.startDate, 'yyyy-MM-dd', { zone: 'utc' }).toISO();
		const endDate = DateTime.fromFormat(timeTrackingEntry.endDate, 'yyyy-MM-dd', { zone: 'utc' }).toISO();

		const standAloneRegistration = timeTrackingEntry.addonLines?.some(a => a.paymentSupplementCode && shiftSupplements.includes(a.paymentSupplementCode));

		const onCall = timeTrackingEntry.onCall || timeTrackingEntry.onCallback;

		try {
			await createTimeTracking({
				variables: {
					timeTrackingDay: {
						user: timeTrackingDay.user,
						date: dayDate.toISO(),
						isPartOfRange: timeTrackingDay.isPartOfRange,
						rangeEndDate: convertedRangedEndDateToISO,
					},
					timeTrackingEntry: {
						user: timeTrackingDay.user,
						startDate,
						endDate,
						startTime: standAloneRegistration ? '00:00' : timeTrackingEntry.startTime.split('D')[0],
						endTime: standAloneRegistration ? '00:00' : timeTrackingEntry.endTime.split('D')[0],
						hours: standAloneRegistration ? 0 : timeTrackingEntry.hours,
						jobNo: timeTrackingEntry.jobNo,
						jobTaskName: timeTrackingEntry.jobTaskName,
						jobTaskNo: timeTrackingEntry.jobTaskNo,
						case: timeTrackingEntry.case,
						drivingSlip: timeTrackingEntry.drivingSlip === '' ? null : timeTrackingEntry.drivingSlip,
						onCall: onCall,
						onCallback: timeTrackingEntry.onCallback,
						includesLunch: timeTrackingEntry.includesLunch,
						remark: timeTrackingEntry.remark,
						timeTrackingStatus: timeTrackingEntry.timeTrackingStatus,
						trackingType: TrackingType.Timeregistration,
						workTypeCode: timeTrackingEntry.workTypeCode,
						addonLines: timeTrackingEntry.addonLines?.map(line => ({
							...line,
							hours: line.supplementType === SupplementType.Piecework ? 1 : Number(line.hours),
							pieceworkAmount: line.supplementType === SupplementType.Piecework ? line.hours : 0,
							onCall: onCall,
						})),
					},
				},
				update: (cache, { data }): void => {
					if (typeof data === 'undefined' || data === null || data.createTimeTrackingDay === null) {
						return;
					}

					const cachedRequest = cache.readQuery<GetTimeTrackingsDayForUserQuery, GetTimeTrackingsDayForUserQueryVariables>({
						query: GetTimeTrackingsDayForUserDocument,
						variables: {
							user: timeTrackingDay.user,
							minDate: timeTrackingDay.date,
							maxDate: timeTrackingDay.date,
						},
					});

					if (cachedRequest === null) {
						return;
					}

					if (cachedRequest.timeTrackingsDayForUser !== null) {
						return;
					}

					cache.writeQuery<GetTimeTrackingsDayForUserQuery, GetTimeTrackingsDayForUserQueryVariables>({
						query: GetTimeTrackingsDayForUserDocument,
						variables: {
							user: timeTrackingDay.user,
							minDate: timeTrackingDay.date,
							maxDate: timeTrackingDay.date,
						},
						data: {
							timeTrackingsDayForUser: data.createTimeTrackingDay,
						},
					});
				},
			});
		} catch (e) {
			console.log(e);
		}
	};

	return {
		createTimeTrackingLoading: createTimeTrackingLoading,
		onSubmit,
	};
}

// Update time tracking
export function useUpdateTimeTracking(): {
	updateTimeTrackingLoading: boolean;
	onSubmitUpdate: (timeTrackingDayId: string, timeTrackingEntry: TimeTrackingEntryUpdateInputType) => Promise<void>;
} {
	const [updateTimeTracking, { loading: updateTimeTrackingLoading }] = useUpdateTimeTrackingDayEntryMutation();

	const onSubmitUpdate = async (timeTrackingDayId: string, timeTrackingEntry: TimeTrackingEntryUpdateInputType) => {
		const date = DateTime.fromFormat(timeTrackingEntry.date, 'yyyy-MM-dd', { zone: 'utc' }).toISO();
		const startDate = DateTime.fromFormat(timeTrackingEntry.startDate, 'yyyy-MM-dd', { zone: 'utc' }).toISO();
		const endDate = DateTime.fromFormat(timeTrackingEntry.endDate, 'yyyy-MM-dd', { zone: 'utc' }).toISO();

		const standAloneRegistration = timeTrackingEntry.addonLines?.some(a => a.paymentSupplementCode && shiftSupplements.includes(a.paymentSupplementCode));

		const onCall = timeTrackingEntry.onCall || timeTrackingEntry.onCallback;

		try {
			await updateTimeTracking({
				variables: {
					timeTrackingDayId: timeTrackingDayId ?? '',
					timeTrackingEntry: {
						parentId: timeTrackingDayId ?? '',
						id: timeTrackingEntry.id,
						date,
						startDate,
						endDate,
						user: timeTrackingEntry.user,
						startTime: standAloneRegistration ? '00:00' : timeTrackingEntry.startTime.split('D')[0],
						endTime: standAloneRegistration ? '00:00' : timeTrackingEntry.endTime.split('D')[0],
						hours: standAloneRegistration ? 0 : timeTrackingEntry.hours,
						jobNo: timeTrackingEntry.jobNo,
						jobTaskName: timeTrackingEntry.jobTaskName,
						jobTaskNo: timeTrackingEntry.jobTaskNo,
						case: timeTrackingEntry.case,
						drivingSlip: timeTrackingEntry.drivingSlip === '' ? null : timeTrackingEntry.drivingSlip,
						onCall: onCall,
						onCallback: timeTrackingEntry.onCallback,
						includesLunch: timeTrackingEntry.includesLunch,
						remark: timeTrackingEntry.remark,
						timeTrackingStatus: timeTrackingEntry.timeTrackingStatus,
						trackingType: TrackingType.Timeregistration,
						workTypeCode: timeTrackingEntry.workTypeCode,
						addonLines: timeTrackingEntry.addonLines?.map(line => ({
							parentId: timeTrackingEntry.id,
							user: timeTrackingEntry.user,
							id: line.id,
							case: line.case,
							date: line.date,
							drivingSlip: line.drivingSlip,
							hours: line.supplementType === SupplementType.Piecework ? 1 : Number(line.hours),
							pieceworkAmount: line.supplementType === SupplementType.Piecework ? line.hours : 0,
							jobNo: line.jobNo,
							jobTaskName: line.jobTaskName,
							jobTaskNo: line.jobTaskNo,
							onCall: onCall,
							onCallback: line.onCallback,
							paymentSupplementCode: line.paymentSupplementCode,
							paymentSupplementName: line.paymentSupplementName,
							remark: line.remark,
							supplementType: line.supplementType,
							supplementUom: line.supplementUom,
							timeTrackingStatus: line.timeTrackingStatus,
							workTypeCode: line.workTypeCode,
						})),
					},
				},
				update: (cache, { data }): void => {
					if (typeof data === 'undefined' || data === null || data.updateTimeTrackingDayEntry === null) {
						return;
					}

					const cachedRequest = cache.readQuery<GetTimeTrackingsDayForUserQuery, GetTimeTrackingsDayForUserQueryVariables>({
						query: GetTimeTrackingsDayForUserDocument,
						variables: {
							user: timeTrackingEntry.user,
							minDate: timeTrackingEntry.date,
							maxDate: timeTrackingEntry.date,
						},
					});

					if (cachedRequest === null) {
						return;
					}

					if (cachedRequest.timeTrackingsDayForUser !== null) {
						return;
					}

					cache.writeQuery<GetTimeTrackingsDayForUserQuery, GetTimeTrackingsDayForUserQueryVariables>({
						query: GetTimeTrackingsDayForUserDocument,
						variables: {
							user: timeTrackingEntry.user,
							minDate: timeTrackingEntry.date,
							maxDate: timeTrackingEntry.date,
						},
						data: {
							timeTrackingsDayForUser: data.updateTimeTrackingDayEntry,
						},
					});
				},
			});
		} catch (e) {
			console.log(e);
		}
	};

	return {
		updateTimeTrackingLoading: updateTimeTrackingLoading,
		onSubmitUpdate,
	};
}

// Delete TimeTrackingDay
export function useDeleteTimeTrackingDay(): {
	deleteTimeTrackingLoading: boolean;
	deleteTimeTrackingDay: (dayId: string, rangeDelete?: boolean) => Promise<void>;
} {
	const [deleteTimeTrackingDayMutation, { loading: deleteTimeTrackingLoading }] = useDeleteTimeTrackingDayMutation();

	const { t } = useTranslation();

	const deleteTimeTrackingDay = async (dayId: string, rangeDelete = false) => {
		const confirm = window.confirm(rangeDelete ? t('timeRegistration.wantToDeleteRange') : t('timeRegistration.wantToDeleteDay'));
		if (confirm) {
			try {
				await deleteTimeTrackingDayMutation({
					variables: {
						dayId: dayId,
					},
				});
			} catch (e) {
				console.log(e);
			}
		}
	};

	return {
		deleteTimeTrackingLoading: deleteTimeTrackingLoading,
		deleteTimeTrackingDay,
	};
}

// Delete TimeTrackingDayEntry
export function useDeleteTimeTrackingDayEntry(): {
	deleteTimeTrackingDayEntryLoading: boolean;
	deleteTimeTrackingDayEntry: (dayId: string, entryId: string) => Promise<void>;
} {
	const [deleteTimeTrackingDayEntryMutation, { loading: deleteTimeTrackingDayEntryLoading }] = useDeleteTimeTrackingDayEntryMutation();

	const { t } = useTranslation();

	const deleteTimeTrackingDayEntry = async (dayId: string, entryId: string) => {
		const confirm = window.confirm(t('timeRegistration.wantToDeleteEntry'));
		if (confirm) {
			try {
				await deleteTimeTrackingDayEntryMutation({
					variables: {
						dayId: dayId,
						entryId: entryId,
					},
				});
			} catch (e) {
				console.log(e);
			}
		}
	};

	return {
		deleteTimeTrackingDayEntryLoading: deleteTimeTrackingDayEntryLoading,
		deleteTimeTrackingDayEntry,
	};
}

// Delete TimeTrackingDayEntrySupplement
export function useDeleteTimeTrackingDayEntrySupplement(): {
	deleteTimeTrackingEntrySupplementLoading: boolean;
	deleteTimeTrackingEntrySupplement: (dayId: string, entryId: string, supplementId: string) => Promise<void>;
} {
	const [deleteTimeTrackingEntrySupplementMutation, { loading: deleteTimeTrackingEntrySupplementLoading }] = useDeleteTimeTrackingEntrySupplementMutation();

	const { t } = useTranslation();

	const deleteTimeTrackingEntrySupplement = async (dayId: string, entryId: string, supplementId: string) => {
		const confirm = window.confirm(t('timeRegistration.wantToDeleteSupplement'));
		if (confirm) {
			try {
				await deleteTimeTrackingEntrySupplementMutation({
					variables: {
						dayId: dayId,
						entryId: entryId,
						supplementId: supplementId,
					},
				});
			} catch (e) {
				console.log(e);
			}
		}
	};

	return {
		deleteTimeTrackingEntrySupplementLoading: deleteTimeTrackingEntrySupplementLoading,
		deleteTimeTrackingEntrySupplement,
	};
}

// Re-open rejected timetracking day
export function useOpenRejectedTimeTrackingDay() {
	const [openRejectedTimeTrackingDayMutation, { loading: openRejectedTimeTrackingDayLoading }] = useOpenRejectedTimeTrackingDayMutation();

	const openRejectedTimeTrackingDay = async (variables: OpenRejectedTimeTrackingDayMutationVariables) => {
		try {
			await openRejectedTimeTrackingDayMutation({
				variables,
			});
		} catch (e) {
			console.log(e);
		}
	};

	return {
		openRejectedTimeTrackingDayLoading,
		openRejectedTimeTrackingDay,
	};
}

// Submit day
export function useSubmitDay(): {
	submitLoading: boolean;
	closeDay: (timeTracking: TimeTrackingDayInputType) => Promise<void>;
} {
	const [submitTimeTrackingDay, { loading: submitLoading }] = useSubmitTimeTrackingDayMutation();

	const closeDay = async (timeTracking: TimeTrackingDayInputType) => {
		if (typeof timeTracking === 'undefined' || timeTracking === null) return;

		const convertedDateToISO = DateTime.fromJSDate(new Date(timeTracking.date)).toISO();

		try {
			await submitTimeTrackingDay({
				variables: {
					timeTrackingDay: {
						date: convertedDateToISO,
						user: timeTracking.user,
						isPartOfRange: timeTracking.isPartOfRange,
						rangeEndDate: timeTracking.rangeEndDate,
					},
				},
				update: (cache, { data }): void => {
					if (typeof data === 'undefined' || data === null || data.submitTimeTrackingDay === null) {
						return;
					}

					const cachedRequest = cache.readQuery<GetTimeTrackingsDayForUserQuery, GetTimeTrackingsDayForUserQueryVariables>({
						query: GetTimeTrackingsDayForUserDocument,
						variables: {
							user: timeTracking.user,
							minDate: timeTracking.date,
							maxDate: timeTracking.date,
						},
					});

					if (cachedRequest === null) {
						return;
					}

					if (cachedRequest.timeTrackingsDayForUser !== null) {
						return;
					}

					cache.writeQuery<GetTimeTrackingsDayForUserQuery, GetTimeTrackingsDayForUserQueryVariables>({
						query: GetTimeTrackingsDayForUserDocument,
						variables: {
							user: timeTracking.user,
							minDate: timeTracking.date,
							maxDate: timeTracking.date,
						},
						data: {
							timeTrackingsDayForUser: data.submitTimeTrackingDay,
						},
					});
				},
			});
		} catch (e) {
			console.log(e);
		}
	};

	return {
		submitLoading: submitLoading,
		closeDay,
	};
}

// Reopen day
export function useReopenDay(): {
	reopenLoading: boolean;
	openDay: (timeTracking: TimeTrackingDayInputType) => Promise<void>;
} {
	const [reopenTimeTrackingDay, { loading: reopenLoading }] = useReopenTimeTrackingDayMutation();

	const openDay = async (timeTracking: TimeTrackingDayInputType) => {
		if (typeof timeTracking === 'undefined' || timeTracking === null) return;

		const convertedDateToISO = DateTime.fromJSDate(new Date(timeTracking.date)).toISO();

		try {
			await reopenTimeTrackingDay({
				variables: {
					timeTrackingDay: {
						date: convertedDateToISO,
						user: timeTracking.user,
						isPartOfRange: timeTracking.isPartOfRange,
						rangeEndDate: timeTracking.rangeEndDate,
					},
				},
			});
		} catch (e) {
			console.log(e);
		}
	};

	return {
		reopenLoading: reopenLoading,
		openDay,
	};
}
